import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";

interface Brand {
  id: string | number;
  name: string;
}

interface Preset {
  id: string;
  name: string;
  brandIds: string[];
  isDefault?: boolean;
}

interface GroupPresetsCardProps {
  presets: Preset[];
  allBrands: Brand[];
  onCreatePreset: () => void;
  onEditPreset: (preset: Preset) => void;
  presetToDelete: { id: string; name: string } | null;
  onDeletePreset: (id: string, name: string) => void;
  onPresetToDeleteChange: (val: { id: string; name: string } | null) => void;
  onConfirmDeletePreset: () => void;
}

export function GroupPresetsCard({
  presets,
  allBrands,
  onCreatePreset,
  onEditPreset,
  presetToDelete,
  onDeletePreset,
  onPresetToDeleteChange,
  onConfirmDeletePreset,
}: GroupPresetsCardProps) {
  return (
    <>
      <Card className="flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Group Presets</CardTitle>
              <CardDescription>Quick-filter groups for analytics</CardDescription>
            </div>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs rounded-lg bg-card" onClick={onCreatePreset}>
              <Plus className="h-3.5 w-3.5" />
              New
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {presets.length === 0 ? (
            <div className="p-4 rounded-lg border border-dashed border-border text-center">
              <p className="text-sm text-muted-foreground">No presets yet</p>
              <p className="text-xs text-muted-foreground mt-1">Create a preset to group brands for filtering</p>
            </div>
          ) : (
            <div className="space-y-2">
              {presets.map((preset) => (
                <div key={preset.id} className="p-3 rounded-lg border border-border hover:border-primary/20 transition-colors">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-medium text-foreground">{preset.name}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground" onClick={() => onEditPreset(preset)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      {!preset.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => onDeletePreset(preset.id, preset.name)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {preset.brandIds.map((bId) => {
                      const brand = allBrands.find((b) => String(b.id) === bId);
                      return brand ? (
                        <Badge key={bId} variant="outline" className="text-[10px] text-muted-foreground">{brand.name}</Badge>
                      ) : null;
                    })}
                    {preset.brandIds.length === 0 && (
                      <span className="text-xs text-muted-foreground">No brands</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDeleteDialog
        open={!!presetToDelete}
        onOpenChange={(open) => { if (!open) onPresetToDeleteChange(null); }}
        title={`Delete "${presetToDelete?.name}"?`}
        description="This will permanently remove this brand group preset. This action cannot be undone."
        onConfirm={onConfirmDeletePreset}
      />
    </>
  );
}
